import React from 'react';
import config from '../../config';
import { twitterPageURL } from '../../util/urlHelpers';
import { StaticPage, TopbarContainer } from '../../containers';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  ExternalLink,
} from '../../components';
import { SectionHowItWorks } from '../../components';
import css from './EquipmentRentalAgreementPage.module.css';

const EquipmentRentalAgreementPage = () => {
  const { siteTwitterHandle, siteFacebookPage } = config;
  const siteTwitterPage = twitterPageURL(siteTwitterHandle);

  return (
    <StaticPage
      title="Equipment Rental Agreement"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'EquipmentRentalAgreementPage',
        description: 'Equipment Rental Agreement | Lenddit',
        name: 'Equipment Rental Agreement',
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.staticPageWrapper}>
          <div>
            <h1>Equipment Rental Agreement</h1>
            <p>
              <strong> </strong>
            </p>
            <p>
              The person or entity identified as “<strong>Owner</strong>” and the person identified
              as “<strong>Customer</strong>” or “<strong>Renter</strong>” on the online Reservation
              Details were connected online through a website, mobile application, and/or associated
              services provided by Lenddit, Inc. (collectively, “<strong>Lenddit</strong>” or the “
              <strong>Lenddit Services</strong>”). As part of connecting through the Lenddit
              Services, the Owner and Customer agreed to be bound by the Lenddit Terms of Service
              and incorporated policies. There is important information below that you should review
              and understand, regardless of whether you are an Owner or Customer. The online
              Reservation Details and this Rental Agreement are collectively referred to as the “
              <strong>Agreement</strong>”. Customer’s execution of this Agreement or taking
              possession of the Equipment (whichever occurs first) shall be deemed acceptance of the
              terms herein. Customer rents the Equipment from Owner pursuant to this Agreement,
              which is a true lease.
            </p>
            <h1>1.      Definitions</h1>
            <ol>
              <li>
                “<strong>Authorized Individuals</strong>” means those individuals that Customer
                directly or indirectly allows to use the Equipment, who must be properly trained to
                use the Equipment, at least 18 years old or the legal age of majority in the state,
                whichever is greater and are not under the influence of any drugs, alcohol,
                substances or otherwise impaired.
              </li>
              <li>
                “<strong>Customer</strong>” means the person, or entity identified on the
                Reservation Details as “Customer” and or “Renter”.
              </li>
              <li>
                “<strong>Equipment</strong>” means the equipment identified on the Reservation
                Details, together with all replacements, repairs, additions, attachments and
                accessories and all future Equipment rented.
              </li>
              <li>
                “<strong>Incident</strong>” means any fine, citation, theft, accident, casualty,
                loss, vandalism, injury, death or damage to person or property, claimed by any
                person or entity that appears to have occurred in connection with the Equipment.
              </li>
              <li>
                “<strong>Party</strong>” means Owner or Customer and together both are the “
                <strong>Parties</strong>”.
              </li>
              <li>
                “<strong>Rental Period</strong>” means the period between when the Customer’s
                schedule starts or takes possession, whichever comes first, of the Equipment until
                it is returned to the Owner or otherwise recovered by the Owner.
              </li>
              <li>
                “<strong>Owner</strong>” means the person, or entity identified on the Reservation
                Details as “Owner”.
              </li>
              <li>
                “<strong>Agreement</strong>” means the Equipment Rental Agreement.
              </li>
              <li>
                “<strong>Commission</strong>” means the Rental Fee minus a fee of the gross rental
                price, calculated at a rate of 0-100% of the Rental Fee, with a minimum Commission
                of Five Dollars ($5).
              </li>
              <li>
                “<strong>Rental Fee</strong>” means daily Rental price multiplied by the number of
                days rented plus the Damage Protection, the Commission and any applicable taxes and
                fees.
              </li>
              <li>
                “<strong>Deposit</strong>” means money paid or held to Lenddit as an earnest or
                security for the performance of the Equipment Rental Agreement or other obligation.
              </li>
            </ol>
            <h1>2. Payment</h1>
            <p>
              Customer shall pay for the Rental Fee, including deposits (“
              <strong>Deposit</strong>”), in full, in advance of the Rental Period using a payment
              card approved by Lenddit and the third party (“
              <strong>Customer Card</strong>”) through Lenddit and the third party of Lenddit’s
              choice. Customer consents to the reservation of credit for estimated charges due and
              authorizes Lenddit and third party to process all amounts due, on Customer Card
              (including Equipment rental, taxes, and fees). All charges are subject to audit, and
              either party will promptly pay to or credit the other party for any necessary
              adjustments or corrections to charges as a result of the audit. Customer will also pay
              all direct costs of collection, including attorney fees, and interest at the highest
              rate permitted by law on any past-due amounts. Customer agrees to and acknowledges
              that if there are any additional costs associated with the Equipment Rental Agreement,
              that Customer will be charged on the Customer Card to reimburse Owner and or Lenddit.
            </p>

            <h1>3. Deposit</h1>
            <p>
              Customer agrees and warrants that Owner shall determine deposit amount. Customer
              acknowledges that the deposit may not be returned in whole or in part (a) if Equipment
              is returned unclean, (b) if damage exceeds the Damage Protection amount of one
              thousand dollars ($1,000), (c) if it is determined that Customer violated the
              Equipment Rental Agreement, (d) if Equipment is utilized in a prohibitive manner,
              damaging the equipment and (e) if any other incident occurs to the Equipment.
            </p>

            <h1>4. Title</h1>
            <p>
              This Agreement is not a contract of sale, and title to the Equipment shall at all
              times remain with Owner. Customer shall keep the Equipment free and clear of all
              mechanics and other liens and encumbrances.
            </p>

            <h1>5. Inspection of Equipment</h1>
            <p>
              {' '}
              Customer acknowledges that Customer has inspected the Equipment prior to taking
              possession thereof, finds it in good working order and repair, and suitable for
              Customer's needs. If Customer discovers any malfunction or defect in Equipment,
              Customer shall promptly notify Owner. Customer shall abide by all third-party
              manufacturer requirements regarding use, repair, maintenance, and notice of Equipment
              while in Customer’s possession. Customer agrees to (a) perform routine inspections on
              the Equipment, including inspections on leaks, cooling systems, water batteries,
              cutting edges, and cleaning in accordance with the manufacturer’s specifications, (b)
              maintain proper fuel, lubrication levels or oil in the Equipment.
            </p>

            <h1>6. Permitted Use</h1>
            <p>
              Customer agrees and warrants that (a) Owner has no control over the manner in which
              the Equipment is operated during the Rental Period by Customer or any third party that
              Customer implicitly or explicitly permits, (b) prior to each use and its return to
              Owner, Customer shall inspect the Equipment to confirm that the Equipment is in good
              condition, without defects, readable decals are on the Equipment, and the Equipment is
              suitable for Customer’s intended use; (c) Customer has access to and reviews the
              operating and safety instructions (the “care guide”, “owner’s manual”, “operator
              manual” or any other type of manual), that could be found on the internet or presented
              to you from the Owner of the Equipment, and will operate the Equipment in accordance
              with the manufacturer’s instructions and with applicable safety equipment; (d)
              Customer shall immediately stop use and notify Owner if the Equipment is damaged,
              unsafe, disabled, malfunctioning, warning lights come on, levied upon, threatened with
              seizure, lost, or if any Incident occurs; (e) Customer has received from Owner all
              information needed or requested regarding the operation of the Equipment; (f) Owner is
              not responsible for providing operator or other training unless Customer specifically
              requests in writing and Owner agrees to provide such training (Customer being
              responsible to obtain all training that Customer desires prior to the Equipment’s
              use); (g) Customer is responsible for the Equipment and its use during the Rental
              Period regardless of the user; (h) the Equipment shall be used and maintained in a
              careful manner, within the Equipment’s capacity and in compliance with all applicable
              laws, regulations, as well as all operating and safety instructions provided on, in or
              with the Equipment and all applicable federal, state and local laws, permits and
              licenses, including but not limited to, OSHA and ADA, as revised; (i) the Equipment
              shall be kept in a secure location; and (j) Customer shall provide Owner with accurate
              and complete information, which Owner relies upon to provide the Equipment to
              Customer. If the manual is not found on the internet and the “Owner” cannot present
              you with a copy of the manual, you can opt out of the rental before the rental starts
              with full explanation to said owner and an email to Lenddit at{' '}
              <a href="mailto:support@lenddit.com">support@lenddit.com</a>. If Owner can find the
              manual on the internet and present it to Lenddit, then the Renter will be charged the
              original rental cost plus taxes and fees.
            </p>

            <h1>7. Delivery</h1>
            <p>
              Customer agrees and warrants that (a) Owner shall agree to exchange or deliver the
              Equipment at Owners discretion, (b) Owner may charge additional fees to Customer to
              provide a delivery service (c) Lenddit has no influence in determining the delivery
              time or method to which Owner delivers Equipment.
            </p>

            <h1>8. Prohibited Use</h1>
            <p>
              <strong>Prohibited Use</strong>. Customer shall not (a) alter or cover up any decals
              or insignia on the Equipment, remove any operating or safety equipment or instructions
              or alter or tamper with the Equipment; (b) use the Equipment in a negligent, illegal,
              unauthorized or abusive manner; (c) publicize use of the Equipment in any manner
              (including, without limitation, print, audiovisual or electronic); or (d) allow the
              use of the Equipment by anyone other than Authorized Individuals (Customer
              acknowledging that the Equipment may be dangerous if used improperly or by untrained
              parties), (d) operate the Equipment while intoxicated or under the influence of any
              substance the impairs Customer’s ability, and (e) remove, operate, or utilize the
              Equipment outside of the United States or Canada.
            </p>

            <h1>9. Malfunctioning Equipment</h1>
            <p>
              Should the Equipment become unsafe, malfunction, or require repair, Customer shall
              immediately cease using the Equipment and immediately notify Owner. If such condition
              is the result of normal operation, Owner will repair or replace the Equipment with
              reasonably similar Equipment in working order, if such replacement Equipment is
              available. Owner has no obligation to repair or replace Equipment rendered inoperable
              by misuse, abuse or neglect of the Customer or its Authorized Individuals. Customer's
              sole remedy for any failure or defect in the Equipment shall be the termination of any
              rental charges accruing after the time of failure or defect. Customer must return the
              Equipment to the Owner within twenty-four (24) hours from the time of failure or
              defect in order to terminate rental charges.
            </p>

            <h1>10. Incidents</h1>
            <p>
              After an Incident, Customer shall: (a) immediately notify the Owner of the Incident
              (b) secure and maintain the Equipment and the surrounding premises in the condition
              existing at the time of such Incident; (c) immediately submit copies of all police or
              other third-party reports to Owner surrounding the Incident. Owner shall have the
              immediate right, but not obligation, to reclaim any Equipment involved in any
              Incident.
            </p>

            <h1>11. Return of Equipment.</h1>
            <ol>
              <li>
                <strong>General</strong>. At the expiration of the Rental Period, Customer shall
                return the Equipment to Owner at the location identified on the Reservation Details.
                Customer is obligated to return the Equipment in the same condition as when Customer
                took possession, reasonable wear and tear, excepted. Customer shall be responsible
                for all damages to or loss of the Equipment during the Rental Period. The following,
                without limitation, shall not be considered reasonable wear and tear: (1) damage
                resulting from lack of lubrication, insertion of improper fuel or maintenance of
                necessary oil, water and air pressure levels; cavitation; or freezing; (2) damage
                resulting from any collision, overturning or improper operation, including
                overloading or exceeding the rated capacity of the Equipment; (3) damage in the
                nature of dents, bending, tearing, staining, corrosion or misalignment to or of the
                Equipment or any part thereof; (4) wear resulting from use in excess of the Rental
                Period for which it was rented; and (5) any other damage to the Equipment which is
                not considered ordinary and reasonable in the equipment rental industry.
              </li>
              <li>
                <strong>Late Return. </strong>Extensions to Reservation Period are at the Owner’s
                option. Customer agrees that if the Equipment is not returned by the end of the
                Rental Period, Owner, in its sole discretion, may require Customer to do any of the
                following: (1) continue to pay the rental rate(s) applicable to the Equipment as
                specified in the Agreement; (2) for periods less than 24 hours, pay the full daily
                rental rate applicable to the Equipment; (3) pay any increased rental rate(s) in
                effect at the time of, or after, the expiration of the Rental Period. If the
                Customer does not contact the Owner, the Owner may determine that the Equipment is
                no longer in the Customer’s possession, Customer will be deemed unlawfully in
                possession of the Equipment, and the Owner and or Lenddit, may among other remedies,
                seek the issuance of a warrant for the arrest if Customer or any other person in
                possession of the Equipment.
              </li>
              <li>Further Failure to Return Provisions:</li>

              <table>
                <tbody>
                  <tr>
                    <td width="77">
                      <p>
                        <strong>State</strong>
                      </p>
                    </td>
                    <td width="612">
                      <p>
                        <strong>Special Notice</strong>
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td width="77">
                      <p>Arizona</p>
                    </td>
                    <td width="612">
                      <p>
                        Failure to return the Equipment within seventy-two (72) hours of Rental
                        Period is unlawful and is a class 1 misdemeanor.
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td width="77">
                      <p>Florida</p>
                    </td>
                    <td width="612">
                      <p>
                        According to 812.155, Florida Statutes states “FAILURE TO RETURN HIRED OR
                        LEASED PERSONAL PROPERTY.—Whoever, after hiring or leasing personal property
                        or equipment under an agreement to return the personal property to the
                        person letting the personal property or equipment or his or her agent at the
                        termination of the period for which it was let, shall, without the consent
                        of the person or persons knowingly abandon or refuse to return the personal
                        property or equipment as agreed, commits a misdemeanor of the second degree,
                        punishable as provided in s. 775.082 or s. 775.083, unless the value of the
                        personal property or equipment is of a value of $300 or more; in that case
                        the person commits a felony of the third degree, punishable as provided in
                        s. 775.082, s. 775.083, or s. 775.084.”
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>

              <li>
                <strong>Lost or Destroyed Equipment</strong>. In the case of the loss or destruction
                of any Equipment, or inability or failure to return same to Owner for any reason
                whatsoever, Customer will pay Owner the then full replacement list value of the
                Equipment together with the full rental rate as specified until such Equipment is
                replaced.
              </li>
              <li>
                <strong>Damaged Equipment</strong>. If the Equipment is returned in a damaged or
                excessively worn condition, Customer shall pay Owner the reasonable cost of repair
                and pay rental on the Equipment at the regular rental rate until all repairs have
                been completed (pursuant to this section 14 &amp; 16).
              </li>
              <li>
                <strong>Remedy</strong>. Customer agrees that Owner and or Lenddit reserves the
                right to charge the Credit Card and/or Customer Card for any amount owed by Customer
                pursuant to this Section 10. Customer agrees to and acknowledges that if there are
                any additional costs associated with the Equipment Rental Agreement, that Customer
                will be charged on the Customer Card to reimburse the Owner and or Lenddit.
              </li>

              <h1>12. Refueling Serving Charge</h1>
              <p>
                If applicable, Customer acknowledges that a “Refueling Service Charge” will be
                applied to all Equipment not returned with a full tank of fuel. The exact cost of
                the Refueling Service Charge may vary depending on the rate being charged on the
                date Customer returns the Equipment. Customer acknowledges that the Refueling
                Service Charge is not a retail sale of fuel. Customer may avoid the Refueling
                Service Charge if Customer returns the Equipment with a full tank of fuel.
              </p>

              <h1>13. Customer Personal Property</h1>
              <p>
                With respect to any personal property left in or on the Equipment upon expiration of
                the Rental Period, Owner is not a bailee or warehouseman of Customer’s, or any other
                person’s, personal property. Owner expressly disclaims any custody, control, or
                responsibility for the care of Customer’s, or any other person’s, personal property.
                Owner may remove the personal property from the Equipment. Under no circumstances is
                Owner responsible for any personal property that may be lost, stolen, or damaged.
                Customer personal property left in or on the Equipment upon expiration of the Rental
                Period may be considered abandoned, unclaimed property in accordance with applicable
                state law.
              </p>

              <h1>14. Repairs</h1>
              <p>
                If applicable, in the event the damage to the Equipment exceeds the Damage
                Protection coverage amount of one thousand dollars ($1,000), and the deductible of
                10% of replacement value of the Equipment or 10% of the cost of repairs, or “
                <strong>Prohibited use</strong>” of Equipment was discovered, Customer agrees to any
                or additional “<strong>Repair Costs</strong>” for the remaining loss of and damage
                to the Equipment that includes but not limited to the Equipment, plus actual towing,
                storage, impound and other related expenses. The Repair Costs will not exceed the
                fair market value of the Equipment and applicable fees. Customer acknowledges and
                agrees to pay the Repair Cost and authorizes Lenddit to charge the Repair Costs to
                the Customer Card. All maintenance or repairs must be performed by a third party the
                Owner or Lenddit approves.
              </p>

              <h1>15. Global Positioning System (“GPS”)</h1>
              <p>
                The Equipment may be equipped with a GPS or similar device that enables the Owner or
                Lenddit to monitor the use and location of the Equipment. The GPS is utilized to
                gather Equipment’s location (determined by GPS systems), mileage, fuel consumption,
                system status information. The Owner and Lenddit may use the information for various
                purposes, including, but not limited to (a) enforcing the terms of this Agreement,
                (b) locating lost or stolen Equipment, (c) providing Customer with support,
                assistance, or services. The Owner and or Lenddit may share this information with
                third parties performing services for the Owner or Lenddit and as deemed necessary,
                and (d) notifying with the legal process or a request from a government entity.
              </p>
              <p id="damage-protection"> </p>

              <p> </p>

              <h1>16. Damage Protection</h1>

              <p>
                Lenddit provides Damage protection, with respect to a deductible of 10% of
                replacement value of the Equipment or 10% of the cost of repairs, on all rentals to
                relieve Customer of repair charges, replacement charges and Administrative Charges
                up to one thousand dollars ($1,000), if the Equipment is damaged during normal use
                during the Rental Period ("Damage Protection"). The charge for Damage Protection is
                10%-35% of the Rental Price and will appear as a separate line-item on the Agreement
                and on the invoice.{' '}
                <strong>
                  <u>Damage Protection is not insurance and Lenddit may make</u>{' '}
                  <u>a profit on its Damage Protection</u>
                </strong>
                . Damage Protection does not cover loss of or damage to the Equipment caused by
                theft, abuse, or intentional acts. Customer expressly acknowledges and agrees that
                Customer will be responsible for all loss or damage. Damage Protection does not
                cover cleaning costs and or fees associated with returning unclean equipment.
              </p>

              <h1>17. Cleaning</h1>
              <p>
                Equipment must be returned to the Owner in a 'rent-ready' condition, meaning it
                should be full of fuel and/or necessary fluids and reasonably clean. If the
                equipment is returned dirty, a cleaning fee will be charged to the Renter,
                determined by the Owner and Lenddit. The Customer acknowledges that a) the deposit
                may be withheld, in whole or in part, if the equipment is returned unclean or b) an
                additional fee will be charged to the Renters credit card on file. Lenddit may
                charge a specific fee, referred to as the 'Cleaning Fee,' to cover the cost of
                cleaning the tools or equipment. This fee is applicable to all rentals and is
                designed to relieve the Owners and Lenddit of cleaning charges and administrative costs. The
                Cleaning Fee is capped at five hundred dollars ($500) and will be applied if the
                equipment is returned unclean at the end of the Rental Period.
              </p>

              {/*<p>
                Equipment should be returned to Owner “rent ready”, i.e. full of fuel and/or fluids
                and reasonably clean. If returned dirty, a cleaning fee will be charged to Renter in
                the amount shown on the Equipment’s listing page at time of rental.
                </p>*/}

              <h1>18. California Proposition 65 Warning Notification Requirement</h1>
              <p>
                Customer acknowledges that Equipment can expose Customer to chemicals including
                benzene, which are known to the State of California to cause birth defects or other
                reproductive harm. For more information go to <a href="http://www/">www.</a>{' '}
                <u>P65Warnings.ca.gov</u>.
              </p>

              <h1>19. LIABILITY</h1>
              <p>
                DURING THE RENTAL PERIOD, CUSTOMER ASSUMES ALL RISK ASSOCIATED WITH THE POSSESSION,
                CONTROL OR USE OF THE EQUIPMENT, INCLUDING BUT NOT LIMITED TO, PERSONAL INJURY,
                DEATH, RENTAL CHARGES, THEFT, LOSSES, DAMAGES AND DESTRUCTION, INCLUDING CUSTOMER
                TRANSPORTATION, LOADING AND UNLOADING, WHETHER OR NOT THE CUSTOMER IS AT FAULT.
                CUSTOMER EXPRESSLY ASSUMES ALL RISK OF DAMAGE TO THE EQUIPMENT OVER ONE THOUSAND
                DOLLARS ($1,000), WITH RESPECT TO A DEDUCTIBLE OF 10% OF REPLACEMENT VALUE OF THE
                EQUIPMENT OR 10% OF THE COST OF REPAIRS, UNLESS IT’S DISCOVERED CUSTOMER USES THE
                EQUIPMENT IN A PROHIBITIVE USE CASE IN SECTION 8, WHICH CUSTOMER WILL ASSUME ALL
                RISK OF THE DAMAGE TO THE EQUIPMENT. FAILURE TO RETUREN THE EQUIPMENT COULD LEAD TO
                CUSTOMER BEING PROSECUTED FOR A CRIME.
              </p>

              <h1>20. NO WARRANTIES</h1>
              <p>
                OWNER DISCLAIMS ALL REPRESENTATIONS AND WARRANTIES, EXPRESS OR IMPLIED, WITH RESPECT
                TO THE EQUIPMENT, ITS DURABILITY, CONDITION, MERCHANTABILITY, NON-INFRINGEMENT, OR
                FITNESS FOR ANY PARTICULAR PURPOSE. CUSTOMER ACKNOWLEDGES ACCEPTANCE OF THE
                EQUIPMENT ON AN “AS IS, WHERE IS” BASIS, WITH “ALL FAULTS” AND WITHOUT ANY RECOURSE
                WHATSOEVER AGAINST OWNER. CUSTOMER ASSUMES ALL RISKS ASSOCIATED WITH THE EQUIPMENT
                AND RELEASES OWNER FROM ALL LIABILITIES AND DAMAGES (INCLUDING LOST PROFITS,
                PERSONAL INJURY, AND SPECIAL, INCIDENTAL AND CONSEQUENTIAL DAMAGES, EVEN IF ADVISED
                OF THE POSSIBILITY OF SUCH DAMAGES) IN ANY WAY CONNECTED WITH THE EQUIPMENT, ITS
                INSTALLATION, OPERATION OR USE OR ANY DEFECT OR FAILURE THEREOF, A BREACH OF OWNER’S
                OBLIGATIONS HEREIN OR ERRORS OR INACCURACIES IN INFORMATION OBTAINED FROM CUSTOMER
                OR THIRD PARTIES, UPON WHICH OWNER RELIES; PROVIDED HOWEVER, IF CUSTOMER IS A
                CONSUMER UNDER APPLICABLE LAW, THEN NO CONSEQUENTIAL DAMAGES LIMITATION OF INJURIES
                TO PERSONS SHALL APPLY.
              </p>

              <h1>21. INDEMNIFICATION</h1>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY LAW, CUSTOMER INDEMNIFIES, RELEASES, HOLDS OWNER
                HARMLESS AND AT OWNER’S REQUEST, DEFENDS OWNER, FROM AND AGAINST ALL LIABILITIES,
                CLAIMS, LOSSES, DAMAGES, AND EXPENSES (INCLUDING ATTORNEY’S AND/OR LEGAL FEES AND
                EXPENSES) HOWEVER ARISING OR INCURRED, RELATED TO ANY INCIDENT, DAMAGE TO PROPERTY,
                INJURY OR DEATH OF ANY PERSON, CONTAMINATION OR ALLEGED CONTAMINATION, OR VIOLATION
                OF LAW OR REGULATION CAUSED BY OR CONNECTED WITH THE (a) ACCESS, USE, POSSESSION OR
                CONTROL OF THE EQUIPMENT BY CUSTOMER OR ANY THIRD PARTY THAT CUSTOMER IMPLICITLY OR
                EXPLICITLY PERMITS TO ACCESS, USE, POSSESS OR CONTROL THE EQUIPMENT DURING THE
                RENTAL PERIOD OR (b) BREACH OF THIS CONTRACT, WHETHER OR NOT CAUSED IN PART BY THE
                ACTIVE OR PASSIVE NEGLIGENCE OR OTHER FAULT OF ANY PARTY INDEMNIFIED HEREIN AND ANY
                OF THE FOREGOING ARISING OR IMPOSED IN ACCORDANCE WITH THE DOCTRINE OF STRICT OR
                ABSOLUTE LIABILITY. CUSTOMER ALSO AGREES TO WAIVE ITS WORKERS’ COMPENSATION
                IMMUNITY, TO THE EXTENT APPLICABLE. CUSTOMER’S INDEMNITY OBLIGATIONS SHALL SURVIVE
                THE EXPIRATION OR TERMINATION OF THIS CONTRACT. All of Customer’s indemnification
                obligations under this paragraph shall be joint and several.
              </p>

              <h1>22. No Assignment, Lending or Subletting</h1>
              <p>
                Customer shall not sublease, subrent, assign, sell or loan the Equipment, and any
                such action by Customer shall be void. Owner may at any time, without notice to
                Customer, transfer or assign the Agreement or any Equipment or any moneys or other
                benefits due or to become due hereunder.
              </p>

              <h1>23. Miscellaneous</h1>
              <p>
                In case any provision of this Agreement is held invalid, illegal or unenforceable,
                the validity, legality and enforceability of the remaining provisions shall not in
                any way be affected or impaired. The waiver by either party of any right under this
                Agreement or failure to perform or of a breach by the other party shall not be
                deemed a waiver of any other right under the Agreement or of any other breach or
                failure by such other party whether of a similar nature or otherwise. This Agreement
                cannot be altered by another document or oral agreement unless agreed to in writing.
                In the event of any conflict between the terms of this Agreement and the terms of
                the Lenddit Terms of Service, the Lenddit Terms of Service control.
              </p>
            </ol>
          </div>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

export default EquipmentRentalAgreementPage;
